/* eslint-disable no-return-assign */
import { AnimatePresence } from 'framer-motion'
import React, { useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { sendAmplitudeData } from '../../../../services/amplitude'
import { FeathersContext } from '../../../../services/feathers'
import Card from '../Card/Card'
import NoJobs from '../NoJob/NoJob'
import TinderBtns from '../TinderBtns/TinderBtns'

import {
  setApprovedLikes,
  setMatchingOffers,
  setPendingLikes,
  setRejectedLikes,
} from '../../../../redux/reducers/talentSlice'
import { Likecontext } from '../../../../services/likeContext'
import * as s from './Stack.styles'

const Stack = () => {
  const { t } = useTranslation(['common'], { useSuspense: false })
  const dispatch = useDispatch()
  const feathers = useContext(FeathersContext)
  const likeContext = useContext(Likecontext)

  const {
    id: talentId,
    matchingOffers,
    likes,
    candidateIds,
  } = useSelector((state) => state.talent)

  // Here to know when we have rejected offer in the seen list
  const [canGoBack, setCanGoBack] = useState(likes.rejected.length)

  useEffect(() => {
    sendAmplitudeData('Talent - Show Offers', {})
  }, [])

  useEffect(() => {
    setCanGoBack(likes.rejected.length)
  }, [likes])

  // Passing the whole offer so we can properly rollback in case of an error
  const candidateMutation = useMutation(
    // offer can be an offer or a pending like with the offer_id prop populated with the offer
    ({ isMatchingOffer = false, offer, status }) => {
      let offerId
      let companyId

      if (isMatchingOffer) {
        companyId = offer.company_id
        offerId = offer._id
      } else {
        companyId = offer.offer_id.company_id
        offerId = offer.offer_id._id
      }

      const { candidateId } = candidateIds.filter(
        (el) => el.companyId === companyId,
      )[0]

      sendAmplitudeData('Talent - Interact With Offer', {
        Offer_Id: offerId,
        Status: status,
      })

      // Here it's create but in the server we try to look if like already exist
      return feathers.service('lambda-talent').create({
        action: 'updateCandidateLikes',
        candidateId,
        query: {
          companyId,
          talentId,
          offerId,
          status,
        },
      })
    },
    {
      onError: (error, { isMatchingOffer, offer, status }) => {
        if (status === 'rejected') {
          setCanGoBack((prevState) => prevState - 1)
        }
        toast.error(t('common:toasts.error'), {
          duration: 500,
        })
        const slicedLikes = likes.pending.slice()
        let like

        if (isMatchingOffer) {
          like = {
            offer_id: offer,
            candidateStatus: 'pending',
            userStatus: 'pending',
            seenByUser: false,
            seenByCandidate: true,
            talent_id: talentId,
          }
        } else {
          like = offer
        }
        slicedLikes.push(like)
        dispatch(setPendingLikes(slicedLikes))
      },
      onSuccess: (data) => {
        const { candidateStatus, userStatus } = data
        switch (candidateStatus) {
          case 'approved': {
            if (userStatus === 'approved') {
              toast.success("C'est un match !", {
                duration: 2550,
              })
            }
            const approvedLikes = likes.approved.slice()
            approvedLikes.push(data)
            dispatch(setApprovedLikes(approvedLikes))
            return
          }
          case 'rejected': {
            const rejectedLikes = likes.rejected.slice()
            rejectedLikes.unshift(data)
            dispatch(setRejectedLikes(rejectedLikes))
            return
          }
          default: {
            const pendingLikes = likes.pending.slice()
            pendingLikes.push(data)
            dispatch(setPendingLikes(pendingLikes))
          }
        }
      },
      retry: false,
    },
  )

  const swipe = (direction) => {
    const vote = {
      left: 'rejected',
      right: 'approved',
    }
    // Doint this so we can do animation left or right
    likeContext.setValue(vote[direction])

    // If we have an element with status rejected we can go back
    if (direction === 'left') {
      setCanGoBack((prevState) => (prevState += 1))
    }

    let offer
    let isMatchingOffer = false

    // If we still have likes they have priority
    // eslint-disable-next-line no-lonely-if
    if (likes.pending.length) {
      offer = likes.pending[likes.pending.length - 1]
    } else if (matchingOffers?.length) {
      offer = matchingOffers[matchingOffers.length - 1]
      isMatchingOffer = true
    } else {
      return
    }

    // Settings the data for the mutation
    const data = { isMatchingOffer, offer, status: vote[direction] }

    candidateMutation.mutate(data)

    // At this point we just need to remove the offer from matching offers
    // which is the last one and dispatch that to redux
    // TODO: check if redux can just pop directly

    if (isMatchingOffer) {
      const slicedMatchingOffers = [...matchingOffers]
      slicedMatchingOffers.pop()
      dispatch(setMatchingOffers(slicedMatchingOffers))
    } else {
      const slicedLikes = [...likes.pending]
      slicedLikes.pop()
      dispatch(setPendingLikes(slicedLikes))
    }
  }

  const goBack = () => {
    if (canGoBack > 0) {
      const sliceRejected = likes.rejected.slice()
      const removed = sliceRejected.shift()

      candidateMutation.mutate({
        offer: removed,
        status: 'pending',
      })

      likeContext.setValue('rejected')
      dispatch(setRejectedLikes(sliceRejected))
      setCanGoBack((prevState) => (prevState -= 1))
    }
  }

  return (
    <s.JobsDetailsWrapper>
      <AnimatePresence>
        {likes?.pending?.length &&
          likes.pending.map((el, i) => {
            const isTop = i === likes.pending.length - 1
            // eslint-disable-next-line react/no-array-index-key
            return (
              <div style={!isTop ? { display: 'none' } : undefined}>
                <Card
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className="card"
                  job={el.offer_id}
                  superLike={el.userStatus === 'approved'}
                  isTop={isTop}
                >
                  <TinderBtns
                    offers={matchingOffers.concat(likes.pending) || []}
                    swipe={swipe}
                    goBack={goBack}
                    canGoBack={canGoBack > 0}
                    mutating={candidateMutation.isLoading}
                  />
                </Card>
              </div>
            )
          })}
        {likes?.pending?.length === 0 &&
          matchingOffers?.length &&
          // Here we need the index as key for framer-motion
          matchingOffers.map((el, i) => {
            const isTop = i === matchingOffers.length - 1
            return (
              <div style={!isTop ? { display: 'none' } : undefined}>
                <Card
                  // eslint-disable-next-line
                  key={i}
                  job={el}
                  className="card"
                  isTop={isTop}
                >
                  <TinderBtns
                    offers={matchingOffers.concat(likes.pending) || []}
                    swipe={swipe}
                    goBack={goBack}
                    canGoBack={canGoBack > 0}
                    mutating={candidateMutation.isLoading}
                  />
                </Card>
              </div>
            )
          })}
        {likes.pending?.length === 0 && matchingOffers?.length === 0 && (
          <NoJobs />
        )}
      </AnimatePresence>
    </s.JobsDetailsWrapper>
  )
}

export default Stack
